import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useApiPut, useApiDelete } from '../../../../api/hooks';
import { useConfirmationModalStore } from '../../../../stores';
import LabeledInput from '../controls/LabeledInput';
import LabeledSelect from '../controls/LabeledSelect';
import PasswordRules, { isValidPassword } from '../controls/PasswordRules';
import { CloseIcon, LockIcon, PersonFillIcon, SendIcon } from '../../model/icons';

const STUDENTS_URL = '/api/camps/admin/students';

const SEX_OPTIONS = [
    { key: 'MALE', value: 'Мужской' },
    { key: 'FEMALE', value: 'Женский' }
];

// Модалка участника: редактирование профиля и пароля.
export default function StudentModal({ open, onClose, student, teamColor, teams, onSaved }) {
    const [form, setForm] = useState({ name: '', surname: '', sex: '', roomNumber: '', teamId: '' });
    const [newPassword, setNewPassword] = useState('');
    const [tgUnlinked, setTgUnlinked] = useState(false);
    const { open: openConfirm } = useConfirmationModalStore();

    useEffect(() => {
        if (student) {
            // Нормализуем регистр (MALE/male) и допускаем вложенный profile.sex — на случай расхождения формата ответа с контрактом.
            const rawSex = student.sex ?? student.profile?.sex ?? '';
            setForm({
                name: student.name || '',
                surname: student.surname || '',
                sex: rawSex ? String(rawSex).toUpperCase() : '',
                roomNumber: student.roomNumber != null ? String(student.roomNumber) : '',
                teamId: student.teamId ?? ''
            });
            setNewPassword('');
            setTgUnlinked(false);
        }
    }, [student]);

    const saveProfile = useApiPut({
        successMessage: 'Профиль сохранён',
        withPreloader: true,
        onSuccess: () => {
            onSaved?.();
            onClose();
        }
    });
    const updatePassword = useApiPut({ successMessage: 'Пароль изменён', withPreloader: true });
    const unlinkTelegram = useApiDelete({
        successMessage: 'Telegram отвязан',
        withPreloader: true,
        onSuccess: () => {
            setTgUnlinked(true);
            onSaved?.();
        }
    });

    if (!open || !student) return null;

    const setField = (field) => (e) => setForm(prev => ({ ...prev, [field]: e.target.value }));

    const profileValid = form.name.trim() && form.surname.trim();

    const handleSaveProfile = () => {
        if (!profileValid) return;
        saveProfile.mutate({
            url: `${STUDENTS_URL}/${student.id}`,
            data: {
                name: form.name.trim(),
                surname: form.surname.trim(),
                sex: form.sex || null,
                roomNumber: form.roomNumber === '' ? null : Number(form.roomNumber),
                teamId: form.teamId === '' ? null : Number(form.teamId)
            }
        });
    };

    const handleChangePassword = () => {
        if (!isValidPassword(newPassword)) return;
        updatePassword.mutate({
            url: `${STUDENTS_URL}/${student.id}`,
            data: { password: newPassword }
        });
        setNewPassword('');
    };

    const teamOptions = teams.map(team => ({ key: team.id, value: team.name || `Команда ${team.id}` }));

    const hasTelegram = !tgUnlinked && !!student.telegramId;
    const handleUnlinkTelegram = () => openConfirm(
        <div className="confirm-msg">
            <b>Отвязать Telegram?</b>
            <p>Студент перестанет получать уведомления в Telegram и сможет привязать аккаунт заново через бота.</p>
        </div>,
        () => unlinkTelegram.mutate({ url: `${STUDENTS_URL}/${student.id}/telegram` }),
        { confirmText: 'Отвязать' }
    );

    return (
        <div className="scrim show" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
            <div className="modal lg" role="dialog" aria-modal="true">
                <div className="modal-head">
                    <div className="pl-head">
                        <span className="pl-av" style={{ background: teamColor, color: '#fff' }}>
                            <PersonFillIcon />
                        </span>
                        <div>
                            <div className="pl-name">{student.surname} {student.name}</div>
                            {student.roomNumber && <div className="pl-room">Комната {student.roomNumber}</div>}
                        </div>
                    </div>
                    <button type="button" className="x-btn" onClick={onClose} aria-label="Закрыть">
                        <CloseIcon />
                    </button>
                </div>

                <div className="modal-body">
                    <div className="set-row2">
                        <LabeledInput label="Имя" value={form.name} onChange={setField('name')} />
                        <LabeledInput label="Фамилия" value={form.surname} onChange={setField('surname')} />
                    </div>
                    <div className="set-row2">
                        <LabeledSelect
                            label="Пол"
                            placeholder="Не указан"
                            options={SEX_OPTIONS}
                            value={form.sex}
                            onChange={setField('sex')}
                        />
                        <LabeledInput
                            label="Комната"
                            type="number"
                            value={form.roomNumber}
                            onChange={setField('roomNumber')}
                        />
                    </div>
                    <LabeledSelect
                        className="set-field"
                        label="Команда"
                        placeholder="Без команды"
                        options={teamOptions}
                        value={form.teamId}
                        onChange={setField('teamId')}
                    />
                    <div className="set-acts">
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={handleSaveProfile}
                            disabled={!profileValid || saveProfile.isPending}
                        >
                            Сохранить профиль
                        </button>
                    </div>

                    <div className="pl-passlabel"><LockIcon />Изменить пароль</div>
                    <div className="pl-passrow">
                        <input
                            className="input"
                            type="password"
                            placeholder="Новый пароль"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                        />
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={handleChangePassword}
                            disabled={!isValidPassword(newPassword) || updatePassword.isPending}
                        >
                            Сохранить
                        </button>
                    </div>
                    <PasswordRules password={newPassword} />

                    <div className="pl-passlabel"><SendIcon />Telegram</div>
                    <div className="pl-tgrow">
                        <span className="pl-tgstatus">
                            {hasTelegram ? `Привязан (ID: ${student.telegramId})` : 'Не привязан'}
                        </span>
                        {hasTelegram && (
                            <button
                                type="button"
                                className="btn btn-danger btn-sm"
                                onClick={handleUnlinkTelegram}
                                disabled={unlinkTelegram.isPending}
                            >
                                Отвязать Telegram
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

StudentModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    student: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        userId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        name: PropTypes.string,
        surname: PropTypes.string,
        sex: PropTypes.string,
        teamId: PropTypes.number,
        roomNumber: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        telegramId: PropTypes.string
    }),
    teamColor: PropTypes.string,
    teams: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.number.isRequired,
        name: PropTypes.string
    })),
    onSaved: PropTypes.func
};

StudentModal.defaultProps = { student: null, teamColor: undefined, teams: [], onSaved: undefined };
